import { useEffect } from 'react'

import { useLocalization } from '@ngb-frontend/shared/context'
import { UserType } from '@ngb-frontend/shared/types'

import type {
  AdditionalServiceValue,
  LegacyAdditionalServiceValue,
  MalfunctionCode,
  QueryContext,
  ServiceValue,
} from '@ngb-frontend/shared/types'

interface PageViewEventDescriptor {
  /** Item name in local language */
  displayName: string
  services?: ServiceValue[]
  additionalServices?: LegacyAdditionalServiceValue[] | AdditionalServiceValue[]
  malfunctions?: MalfunctionCode[]
}

declare global {
  interface Window {
    dataLayer?: Array<
      Record<
        string,
        string | number | boolean | Record<string, string | boolean>
      >
    >
  }
}

export interface Tracker {
  trackPageView: (pageViewEvent: PageViewEventDescriptor) => void
}

const stringifyCollection = (data: string[]) => {
  return data.join(';')
}

export const useTracking = (query?: QueryContext): Tracker => {
  const { locale } = useLocalization()

  useEffect(() => {
    window.dataLayer = window.dataLayer || []
  }, [])

  const trackPageView = ({
    displayName,
    services,
    additionalServices,
    malfunctions,
  }: PageViewEventDescriptor) => {
    if (!window.dataLayer) {
      return
    }
    const [isoCountry, isoLanguage] = (locale || '').toLowerCase().split('-')

    window.dataLayer.push({
      event: 'SPA PageView',
      eventType: 'pageView',
      userType: query?.userType || UserType.Unknown,
      vehicleId: query?.vehicleIdentifier.value || '',
      pageURL: window.location.href,
      displayName,
      pageTitle: 'NGB',
      valueStream: 'NGB',
      country: isoCountry || '',
      language: isoLanguage || '',
      ...(services ? { services: stringifyCollection(services) } : {}),
      ...(additionalServices
        ? { additionalServices: stringifyCollection(additionalServices) }
        : {}),
      ...(malfunctions
        ? { malfunctions: stringifyCollection(malfunctions) }
        : {}),
    })
  }

  return { trackPageView }
}
